import { useParams, Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Pencil } from "lucide-react";
import { MarkdocRenderer } from "@/components/markdoc/MarkdocRenderer";
import { useDocSections } from "@/hooks/useDocSections";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { docSections as staticSections } from "@/content/docs";

export default function DocsSection() {
  const { section } = useParams<{ section: string }>();
  const { data: sections } = useDocSections();
  const { isAdmin } = useAdminAuth();

  const allSections = sections || staticSections;
  const idx = allSections.findIndex((s) => s.slug === section);

  if (idx === -1) return <Navigate to="/docs/getting-started" replace />;

  const current = allSections[idx];
  const prev = idx > 0 ? allSections[idx - 1] : null;
  const next = idx < allSections.length - 1 ? allSections[idx + 1] : null;
  const Icon = current.icon;

  return (
    <>
      {/* Mobile nav */}
      <nav className="lg:hidden mb-8">
        <details className="rounded-lg border border-border bg-muted/30">
          <summary className="px-4 py-3 text-sm font-medium cursor-pointer">
            All Sections
          </summary>
          <ul className="px-4 pb-3 space-y-1">
            {allSections.map((s) => {
              const SIcon = s.icon;
              return (
                <li key={s.slug}>
                  <Link
                    to={`/docs/${s.slug}`}
                    className={`flex items-center gap-2 text-sm py-1.5 px-2 rounded ${
                      s.slug === section
                        ? "text-primary font-medium bg-primary/10"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <SIcon className="h-4 w-4" />
                    {s.title}
                  </Link>
                </li>
              );
            })}
          </ul>
        </details>
      </nav>

      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <h1 className="text-2xl md:text-3xl font-display font-bold">{current.title}</h1>
        </div>
        {isAdmin && (
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/docs">
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
        )}
      </div>
      <p className="text-muted-foreground mb-8">{current.description}</p>

      {/* Content */}
      <MarkdocRenderer content={current.content} />

      {/* Prev / Next */}
      <div className="flex items-center justify-between mt-14 pt-8 border-t border-border">
        {prev ? (
          <Button asChild variant="ghost" size="sm">
            <Link to={`/docs/${prev.slug}`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {prev.title}
            </Link>
          </Button>
        ) : <span />}
        {next ? (
          <Button asChild variant="ghost" size="sm">
            <Link to={`/docs/${next.slug}`}>
              {next.title}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        ) : <span />}
      </div>
    </>
  );
}
